import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Button,
} from '@mui/material';
import {
  Receipt,
  Package,
  TrendingUp,
  RefreshCw,
  PlusCircle,
  Wallet,
} from 'lucide-react';
import LowStockAlertSmall from './LowStockAlertSmall';
import { 
  AreaChart,
  Area,
  PieChart, 
  Pie, 
  Cell,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Legend 
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import useFirestoreStore from '../stores/useFirestoreStore';

const Home = ({ permissions }) => {
  const navigate = useNavigate();

  // Read from global store — no extra listeners created
  const bills = useFirestoreStore((state) => state.bills);
  const inventory = useFirestoreStore((state) => state.inventory);
  const expenses = useFirestoreStore((state) => state.expenses);
  const creditors = useFirestoreStore((state) => state.creditors);
  const returns = useFirestoreStore((state) => state.returns);
  const billsLoading = useFirestoreStore((state) => state.loading.bills);
  const inventoryLoading = useFirestoreStore((state) => state.loading.inventory);

  const loading = billsLoading || inventoryLoading;
  const [refreshing, setRefreshing] = useState(false);

  // Check if user can view profit/loss data
  const canViewProfitLoss = permissions?.userType === 'admin' || permissions?.viewProfitLoss === true;

  const handleRefresh = () => {
    // Data is kept live by the global store; a brief visual feedback is enough
    setRefreshing(true);
    setTimeout(() => setRefreshing(false), 600);
  };

  // Helper: local date string YYYY-MM-DD
  const localDateStr = (d = new Date()) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  // Calculate today's sales (gross - returns processed today)
  const getTodaySales = () => {
    const today = localDateStr();
    const grossToday = bills
      .filter(bill => bill.date === today)
      .reduce((sum, bill) => sum + (bill.total || 0), 0);
    const returnsToday = returns
      .filter(ret => ret.date === today)
      .reduce((sum, ret) => sum + (ret.refundAmount || 0), 0);
    return grossToday - returnsToday;
  };

  // Calculate stock value
  const getStockValue = () => {
    return inventory.reduce((sum, item) => {
      return sum + ((item.costPrice || 0) * (item.stock || 0));
    }, 0);
  };

  // Calculate current month P&L
  const getCurrentMonthPL = () => {
    const currentMonth = localDateStr().slice(0, 7);

    const grossRevenue = bills
      .filter(bill => bill.date && bill.date.startsWith(currentMonth))
      .reduce((sum, bill) => sum + (bill.total || 0), 0);

    const monthReturns = returns
      .filter(ret => ret.date && ret.date.startsWith(currentMonth))
      .reduce((sum, ret) => sum + (ret.refundAmount || 0), 0);

    const revenue = grossRevenue - monthReturns;

    // COGS — exclude returned items
    const returnedBillIds = new Set(
      returns.filter(r => r.date?.startsWith(currentMonth)).map(r => r.billId)
    );
    const returnedItemsMap = {};
    returns.filter(r => r.date?.startsWith(currentMonth)).forEach(ret => {
      if (!returnedItemsMap[ret.billId]) returnedItemsMap[ret.billId] = {};
      (ret.items || []).forEach(item => {
        const id = item.inventoryId || item.id;
        returnedItemsMap[ret.billId][id] = (returnedItemsMap[ret.billId][id] || 0) + (item.quantity || 0);
      });
    });

    const cogs = bills
      .filter(bill => bill.date && bill.date.startsWith(currentMonth))
      .reduce((sum, bill) => {
        if (returnedBillIds.has(bill.id) && bill.returnStatus === 'Fully Returned') return sum;
        let billCost = (bill.items || []).reduce((itemSum, item) => {
          const invId = item.inventoryId || item.id;
          const invItem = inventory.find(inv => inv.id === invId || inv.name === item.name);
          const mlFactor = item.isDecant && item.decantML ? item.decantML : 1;
          return itemSum + ((invItem?.costPrice || 0) * mlFactor * (item.quantity || 0));
        }, 0);
        if (returnedBillIds.has(bill.id) && returnedItemsMap[bill.id]) {
          const returnedCOGS = (bill.items || []).reduce((itemSum, item) => {
            const invId = item.inventoryId || item.id;
            const returnedQty = returnedItemsMap[bill.id][invId] || returnedItemsMap[bill.id][item.id] || 0;
            if (!returnedQty) return itemSum;
            const invItem = inventory.find(inv => inv.id === invId || inv.name === item.name);
            const mlFactor = item.isDecant && item.decantML ? item.decantML : 1;
            return itemSum + ((invItem?.costPrice || 0) * mlFactor * returnedQty);
          }, 0);
          billCost = Math.max(0, billCost - returnedCOGS);
        }
        return sum + billCost;
      }, 0);

    const monthExpenses = expenses
      .filter(exp => exp.date && exp.date.startsWith(currentMonth))
      .reduce((sum, exp) => sum + (exp.grandTotal || exp.amount || 0), 0);

    const grossProfit = revenue - cogs;
    const netProfit = grossProfit - monthExpenses;

    return { revenue, cogs, grossProfit, expenses: monthExpenses, netProfit };
  };

  // Get monthly sales data for chart (last 6 months)
  const getMonthlySalesData = () => {
    const months = [];
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    for (let i = 5; i >= 0; i--) {
      const date = new Date();
      date.setMonth(date.getMonth() - i);
      const monthStr = localDateStr(date).slice(0, 7);
      const monthName = monthNames[date.getMonth()];
      
      const monthSales = bills
        .filter(bill => bill.date && bill.date.startsWith(monthStr))
        .reduce((sum, bill) => sum + (bill.total || 0), 0);

      const monthReturnsAmt = returns
        .filter(ret => ret.date && ret.date.startsWith(monthStr))
        .reduce((sum, ret) => sum + (ret.refundAmount || 0), 0);

      const netMonthSales = monthSales - monthReturnsAmt;
      
      const monthCost = bills
        .filter(bill => bill.date && bill.date.startsWith(monthStr))
        .reduce((sum, bill) => {
          const billCost = (bill.items || []).reduce((itemSum, item) => {
            const invId = item.inventoryId || item.id;
            const invItem = inventory.find(inv => inv.id === invId || inv.name === item.name);
            const mlFactor = item.isDecant && item.decantML ? item.decantML : 1;
            return itemSum + ((invItem?.costPrice || 0) * mlFactor * (item.quantity || 0));
          }, 0);
          return sum + billCost;
        }, 0);
      
      const monthExpenses = expenses
        .filter(exp => exp.date && exp.date.startsWith(monthStr))
        .reduce((sum, exp) => sum + (exp.grandTotal || exp.amount || 0), 0);
      
      months.push({
        month: monthName,
        sales: netMonthSales,
        profit: netMonthSales - monthCost - monthExpenses
      });
    }
    
    return months;
  };

  // Get creditor receivables data with Credit/COD breakdown
  const getReceivablesData = () => {
    const creditDue = bills
      .filter(bill => bill.status === 'Pending' && bill.paymentType === 'Credit')
      .reduce((sum, bill) => sum + (bill.total || 0), 0);

    const codDue = bills
      .filter(bill => bill.status === 'Pending' && bill.paymentType === 'COD')
      .reduce((sum, bill) => sum + (bill.total || 0), 0);

    const kokoDue = bills
      .filter(bill => bill.status === 'Koko' || bill.paymentType === 'Koko')
      .reduce((sum, bill) => sum + (bill.total || 0), 0);

    const mintPayDue = bills
      .filter(bill => bill.status === 'MintPay' || bill.paymentType === 'MintPay')
      .reduce((sum, bill) => sum + (bill.total || 0), 0);

    const totalPaid = creditors.reduce((sum, c) => sum + (c.totalPaid || 0), 0);

    return [
      { name: 'Credit Due', value: creditDue, color: '#ef4444' },
      { name: 'COD Due', value: codDue, color: '#f87171' },
      { name: 'Koko Due', value: kokoDue, color: '#f59e0b' },
      { name: 'MintPay Due', value: mintPayDue, color: '#fb923c' },
      { name: 'Received', value: totalPaid, color: '#22c55e' },
    ].filter(item => item.value > 0);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  const todaySales = getTodaySales();
  const stockValue = getStockValue();
  const monthPL = getCurrentMonthPL();
  const monthlySalesData = getMonthlySalesData();
  const receivablesData = getReceivablesData();

  const StatCard = ({ icon, value, label, color, subtitle }) => (
    <Card sx={{ height: '100%', position: 'relative', overflow: 'visible' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              backgroundColor: 'action.hover',
              color: 'text.primary',
              display: 'flex',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            {icon}
          </Box>
        </Box>
        <Typography variant="h4" fontWeight={700} gutterBottom color="text.primary">
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary" fontWeight={600}>
          {label}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Real-time business overview and insights
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={<PlusCircle size={18} />}
            onClick={() => navigate('/billing')}
            size="small"
            sx={{ 
              minWidth: { xs: 'auto', sm: 120 },
              backgroundColor: 'error.main',
              color: 'common.white',
              '&:hover': {
                backgroundColor: 'error.dark',
              }
            }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>New Invoice</Box>
            <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>New</Box>
          </Button>
          <Button
            variant="outlined"
            startIcon={refreshing ? <CircularProgress size={20} /> : <RefreshCw size={18} />}
            onClick={handleRefresh}
            disabled={refreshing}
            size="small"
            color="primary"
            sx={{ 
              minWidth: { xs: 'auto', sm: 100 },
            }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Refresh</Box>
          </Button>
        </Box>
      </Box>

      {/* Global Low Stock Alert */}
      <LowStockAlertSmall />

      {/* Pending Receivables Alert Banner */}
      {bills.filter(bill => (bill.status === 'Pending' && (bill.paymentType === 'Credit' || bill.paymentType === 'COD')) || bill.status === 'Koko' || bill.status === 'MintPay').length > 0 && (
        <Box 
          sx={{ 
            mb: 3, 
            p: 2, 
            backgroundColor: 'error.lighter',
            border: '2px solid',
            borderColor: 'error.main',
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': {
              backgroundColor: 'error.lighter',
              transform: 'translateY(-2px)',
              boxShadow: 2
            }
          }}
          onClick={() => navigate('/creditors')}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box 
              sx={{ 
                width: 48, 
                height: 48, 
                borderRadius: '50%', 
                backgroundColor: 'error.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'common.white'
              }}
            >
              <Wallet size={24} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700} color="text.primary">
                LKR {bills
                  .filter(bill => (bill.status === 'Pending' && (bill.paymentType === 'Credit' || bill.paymentType === 'COD')) || bill.status === 'Koko' || bill.status === 'MintPay')
                  .reduce((sum, bill) => sum + (bill.total || 0), 0)
                  .toLocaleString('en-LK', { minimumFractionDigits: 2 })} Pending Receivables
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {bills.filter(bill => (bill.status === 'Pending' && (bill.paymentType === 'Credit' || bill.paymentType === 'COD')) || bill.status === 'Koko' || bill.status === 'MintPay').length} unpaid invoice{bills.filter(bill => (bill.status === 'Pending' && (bill.paymentType === 'Credit' || bill.paymentType === 'COD')) || bill.status === 'Koko' || bill.status === 'MintPay').length > 1 ? 's' : ''} - Click to view details
              </Typography>
            </Box>
          </Box>
          <Button 
            variant="contained" 
            sx={{ 
              backgroundColor: 'error.main',
              color: 'common.white',
              '&:hover': { backgroundColor: 'error.dark' }
            }}
            onClick={(e) => {
              e.stopPropagation();
              navigate('/creditors');
            }}
          >
            View Creditors
          </Button>
        </Box>
      )}

      {/* Top Metrics */}
      <Grid container spacing={4} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={canViewProfitLoss ? 3 : 6}>
          <StatCard
            icon={<Receipt size={24} />}
            value={`LKR ${todaySales.toLocaleString('en-LK', { minimumFractionDigits: 2 })}`}
            label="Today's Sales"
            subtitle="Real-time revenue"
          />
        </Grid>
        {canViewProfitLoss && (
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<Package size={24} />}
              value={`LKR ${stockValue.toLocaleString('en-LK', { minimumFractionDigits: 2 })}`}
              label="Stock Value"
              subtitle="Total inventory worth"
            />
          </Grid>
        )}
        {canViewProfitLoss && (
          <>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                icon={<Wallet size={24} />}
                value={`LKR ${monthPL.grossProfit.toLocaleString('en-LK', { minimumFractionDigits: 2 })}`}
                label="P&L This Month"
                subtitle="Gross profit"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                icon={<TrendingUp size={24} />}
                value={`LKR ${monthPL.netProfit.toLocaleString('en-LK', { minimumFractionDigits: 2 })}`}
                label="Current Month Profit"
                subtitle="After expenses"
              />
            </Grid>
          </>
        )}
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={4}>
        {/* Sales & Profit Chart */}
        {canViewProfitLoss && (
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Sales & Profit Trend
                </Typography>
                <Typography variant="caption" color="text.secondary" gutterBottom display="block" sx={{ mb: 2 }}>
                  Last 6 months performance
                </Typography>
                <ResponsiveContainer width="100%" height={350}>
                  <AreaChart data={monthlySalesData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#c41e3a" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#c41e3a" stopOpacity={0.1}/>
                      </linearGradient>
                      <linearGradient id="colorProfit" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#ffffff" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#ffffff" stopOpacity={0.1}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(128,128,128,0.1)" />
                    <XAxis dataKey="month" />
                    <YAxis 
                      tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`}
                      stroke="rgba(128,128,128,0.5)"
                      style={{ fontSize: '12px' }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'rgba(26, 26, 26, 0.98)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '8px',
                        color: 'common.white',
                      }}
                      labelStyle={{
                        color: 'common.white',
                      }}
                      formatter={(value) => `LKR ${value.toLocaleString('en-LK', { minimumFractionDigits: 2 })}`}
                    />
                    <Legend />
                    <Area 
                      type="monotone" 
                      dataKey="sales" 
                      stroke="#c41e3a" 
                      fillOpacity={1} 
                      fill="url(#colorSales)"
                      name="Sales"
                    />
                    <Area 
                      type="monotone" 
                      dataKey="profit" 
                      stroke="#ffffff" 
                      fillOpacity={1} 
                      fill="url(#colorProfit)"
                      name="Profit"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Customer Receivable Pie Chart */}
        <Grid item xs={12} md={canViewProfitLoss ? 4 : 12}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Customer Receivable
              </Typography>
              <Typography variant="caption" color="text.secondary" gutterBottom display="block" sx={{ mb: 2 }}>
                Credit payment status
              </Typography>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={receivablesData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    fill="#8884d8"
                    paddingAngle={0}
                    dataKey="value"
                  >
                    {receivablesData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value) => `LKR ${value.toLocaleString('en-LK', { minimumFractionDigits: 2 })}`}
                  />
                </PieChart>
              </ResponsiveContainer>
              
              {/* Summary */}
              <Box sx={{ mt: 2 }}>
                {receivablesData.map((item, index) => (
                  <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: item.color }} />
                      <Typography variant="body2">{item.name}</Typography>
                    </Box>
                    <Typography variant="body2" fontWeight={600}>
                      LKR {item.value.toLocaleString('en-LK', { minimumFractionDigits: 2 })}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Home;
